/**
 * WebSocket connection and command dispatch module.
 *
 * This module handles:
 * - WebSocket connection lifecycle (connect, reconnect, heartbeat)
 * - Command dispatch via the messaging system
 * - Request/response correlation via waitForAck
 */

import { hello, ping } from "@interlucent/organization-protocol";
import type { Msg } from "@interlucent/organization-protocol";
import {
  dispatch,
  setDispatcherWebSocket,
  type CommandDefinitions,
} from "./messaging";
export { waitForReady } from "./messaging";
import { useStore } from './store';
import { logger } from './log';

const log = logger('org-ws');

// =============================================================================
// CONNECTION STATE
// =============================================================================

export type WsConnectionState = 'connected' | 'disconnected' | 'reconnecting';

// Exponential backoff state
let reconnectAttempts = 0;
const MIN_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 30000;
const BACKOFF_MULTIPLIER = 1.5;

// Keepalive interval state — raw "ping" strings auto-responded by Cloudflare edge
let keepaliveInterval: ReturnType<typeof setInterval> | null = null;
const KEEPALIVE_INTERVAL_MS = 25_000; // 25s, safely under common 60s proxy idle timeouts

// Health check timeout for visibility-change probe (ms)
// Allow enough time for a hibernating Durable Object to wake up
const HEALTH_CHECK_TIMEOUT_MS = 8_000;

// Fast-reconnect flag: skip backoff on first attempt after detecting dead connection
let fastReconnect = false;

// Guard against duplicate health checks
let healthCheckPending = false;

// Track when we last received any message (including pong) for half-open detection
let lastMessageReceivedAt = 0;
const HALF_OPEN_THRESHOLD_MS = 60_000; // 60s without any message = likely dead

// =============================================================================
// HELPERS
// =============================================================================

function getCloseCodeDescription(code: number): string {
  const codes: Record<number, string> = {
    1000: 'Normal closure',
    1001: 'Going away (page navigation)',
    1002: 'Protocol error',
    1003: 'Unsupported data',
    1005: 'No status received',
    1006: 'Abnormal closure (no close frame)',
    1007: 'Invalid payload data',
    1008: 'Policy violation',
    1009: 'Message too big',
    1010: 'Missing extension',
    1011: 'Internal server error',
    1012: 'Service restart',
    1013: 'Try again later',
    1014: 'Bad gateway',
    1015: 'TLS handshake failure',
  };
  return codes[code] || `Unknown (${code})`;
}

function getReconnectDelay(): number {
  const base = Math.min(
    MIN_RETRY_DELAY * Math.pow(BACKOFF_MULTIPLIER, reconnectAttempts),
    MAX_RETRY_DELAY
  );
  // Add +/- 25% jitter to prevent thundering herd after deploys/restarts
  const jitter = base * 0.25 * (Math.random() * 2 - 1);
  return Math.max(0, base + jitter);
}

// =============================================================================
// CONNECTION CALLBACKS
// =============================================================================

export interface ConnectionCallbacks {
  onStateChange?: (state: WsConnectionState, attempt?: number) => void;
  onNewSocket?: (ws: WebSocket) => void;
  onAuthRequired?: () => void;
}

export type TokenGetter = () => Promise<string | null>;

// =============================================================================
// CONNECTION MANAGEMENT
// =============================================================================

export interface WsConnection {
  ws: WebSocket | null;
  dispose: () => void;
}

// Monotonic counter for human-readable connection IDs
let connSeq = 0;
function nextConnId(): string {
  return `c${++connSeq}`;
}

/** Shared mutable state across a connection and all its reconnects. */
interface SharedConnectionState {
  disposed: boolean;
  currentWs: WebSocket | null;
  /** Stable ID across the entire reconnect chain (e.g. "c1"). */
  id: string;
  /** Per-socket counter within a chain (e.g. "c1.2" = 2nd socket in chain c1). */
  socketSeq: number;
}

export async function connectWS(
  tokenOrGetter: string | TokenGetter,
  onMessage: (m: Msg) => void,
  wantSnapshot = true,
  lastSeq = 0,
  callbacks?: ConnectionCallbacks,
  _shared?: SharedConnectionState,
): Promise<WsConnection> {
  // Shared across the entire reconnect chain so that dispose() can reach
  // whichever WebSocket is currently active — not just the first one.
  const shared = _shared ?? { disposed: false, currentWs: null, id: nextConnId(), socketSeq: 0 };
  // Use dedicated dash domain for org WS to avoid Safari/iCloud Private Relay
  // bug with multiple WebSocket connections from the same origin
  const dashUrl = (import.meta as any).env.VITE_DASH_URL || (import.meta as any).env.VITE_API_URL || "wss://api.dev.interlucent.ai";
  const adminOrgOverride = useStore.getState().adminOrgOverride;
  const wsUrl = adminOrgOverride
    ? `${dashUrl}/ws/organization?adminOrgId=${encodeURIComponent(adminOrgOverride)}`
    : `${dashUrl}/ws/organization`;

  // Per-socket ID within this chain (e.g. "c1.1", "c1.2" for reconnects)
  shared.socketSeq++;
  const sid = `${shared.id}.${shared.socketSeq}`;

  // Get token
  let token: string | null;
  if (typeof tokenOrGetter === 'function') {
    log.debug(`[${sid}] Fetching token...`);
    token = await tokenOrGetter();
  } else {
    token = tokenOrGetter;
  }

  const noop = () => {};
  if (!token) {
    log.error(`[${sid}] No token available, cannot connect`);
    callbacks?.onStateChange?.('disconnected');
    callbacks?.onAuthRequired?.();
    return { ws: null, dispose: noop };
  }

  // Token acquisition is async — check if we were disposed while awaiting
  if (shared.disposed) {
    log.info(`[${sid}] Connection disposed during token acquisition, aborting`);
    return { ws: null, dispose: noop };
  }

  log.info(`[${sid}] Connecting to: ${wsUrl} (attempt ${reconnectAttempts + 1})`);
  log.debug(`[${sid}] Using token (first 20 chars): ${token.substring(0, 20)}...`);

  if (reconnectAttempts > 0) {
    callbacks?.onStateChange?.('reconnecting', reconnectAttempts);
  }

  const ws = new WebSocket(wsUrl, [`Bearer.${token}`]);
  shared.currentWs = ws;

  // Set dispatcher's WebSocket
  setDispatcherWebSocket(ws);

  callbacks?.onNewSocket?.(ws);

  ws.onopen = () => {
    log.info(`[${sid}] Connection opened successfully`);
    reconnectAttempts = 0;
    fastReconnect = false;
    callbacks?.onStateChange?.('connected');

    const helloMsg = hello({ lastSeq, wantSnapshot });
    log.debug('Sending hello message', helloMsg);
    ws.send(JSON.stringify(helloMsg));

    // Start keepalive — raw "ping" auto-responded by Cloudflare edge without waking DO
    if (keepaliveInterval) {
      clearInterval(keepaliveInterval);
    }
    lastMessageReceivedAt = Date.now();
    keepaliveInterval = setInterval(() => {
      if (ws.readyState !== WebSocket.OPEN) return;

      // Detect half-open connections: if no message received for 60s despite
      // sending pings every 25s, the connection is likely silently dead.
      if (lastMessageReceivedAt > 0 &&
          Date.now() - lastMessageReceivedAt > HALF_OPEN_THRESHOLD_MS &&
          !healthCheckPending) {
        log.info('No messages received for 60s, triggering health check');
        triggerHealthCheck(ws);
        return;
      }

      ws.send('ping');
    }, KEEPALIVE_INTERVAL_MS);
  };

  ws.onmessage = (ev) => {
    lastMessageReceivedAt = Date.now();

    // Filter raw "pong" auto-responses from Cloudflare edge keepalive
    if (ev.data === 'pong') return;

    log.debug('Received message', ev.data);
    try {
      onMessage(JSON.parse(ev.data));
    } catch (err) {
      log.error('Error parsing message', err);
    }
  };

  ws.onerror = (err) => {
    log.error(`[${sid}] WebSocket error`, err);
  };

  ws.onclose = (ev) => {
    log.info(`[${sid}] Connection closed: code=${ev.code} (${getCloseCodeDescription(ev.code)}) reason="${ev.reason || 'none'}" disposed=${shared.disposed}`);

    // Don't touch shared state if this connection was intentionally disposed —
    // a newer connection may have already replaced our keepalive and dispatcher ref.
    if (shared.disposed) {
      log.info(`[${sid}] Connection disposed, skipping cleanup & reconnect`);
      return;
    }

    if (keepaliveInterval) {
      clearInterval(keepaliveInterval);
      keepaliveInterval = null;
    }

    // Clear dispatcher's WebSocket
    setDispatcherWebSocket(null);

    callbacks?.onStateChange?.('disconnected');

    // Use 0ms delay when fast-reconnect flag is set (e.g., after background health check failure)
    const delay = fastReconnect ? 0 : getReconnectDelay();
    fastReconnect = false;

    log.info(`[${sid}] Reconnecting in ${(delay / 1000).toFixed(1)} seconds... (attempt ${reconnectAttempts + 1})`);
    reconnectAttempts++;

    setTimeout(async () => {
      if (shared.disposed) {
        log.info(`[${sid}] Reconnect aborted — disposed during delay`);
        return;
      }
      callbacks?.onStateChange?.('reconnecting', reconnectAttempts);
      await connectWS(tokenOrGetter, onMessage, wantSnapshot, lastSeq, callbacks, shared);
    }, delay);
  };

  const dispose = () => {
    log.info(`[${sid}] dispose() called (chain=${shared.id}, wasDisposed=${shared.disposed})`);
    shared.disposed = true;
    const target = shared.currentWs;
    if (target && target.readyState !== WebSocket.CLOSED && target.readyState !== WebSocket.CLOSING) {
      log.info(`[${sid}] Closing WebSocket (readyState=${target.readyState})`);
      target.close(1000, 'Disposed');
    } else {
      log.info(`[${sid}] WebSocket already closed/closing (readyState=${target?.readyState ?? 'null'})`);
    }
  };

  return { ws, dispose };
}

/**
 * Sends a JSON Kind.Ping to the DO and waits for any response within
 * HEALTH_CHECK_TIMEOUT_MS. If no response, declares connection dead
 * and triggers a fast reconnect.
 */
function triggerHealthCheck(ws: WebSocket): void {
  if (healthCheckPending) return;

  log.info('Starting health check');
  healthCheckPending = true;

  let timer: ReturnType<typeof setTimeout> | null = null;

  const onAnyMessage = () => {
    // Any message proves the connection is alive
    log.info('Health check passed');
    healthCheckPending = false;
    if (timer) clearTimeout(timer);
    ws.removeEventListener('message', onAnyMessage);
  };

  ws.addEventListener('message', onAnyMessage);

  try {
    ws.send(JSON.stringify(ping()));
  } catch {
    log.warn('Health check send failed, closing connection');
    healthCheckPending = false;
    ws.removeEventListener('message', onAnyMessage);
    fastReconnect = true;
    reconnectAttempts = 0;
    ws.close();
    return;
  }

  // If no message arrives within the timeout, the connection is dead
  timer = setTimeout(() => {
    log.warn('Health check timed out, connection is dead');
    healthCheckPending = false;
    ws.removeEventListener('message', onAnyMessage);
    fastReconnect = true;
    reconnectAttempts = 0;
    ws.close();
  }, HEALTH_CHECK_TIMEOUT_MS);
}

// Handle page visibility changes — perform health check when tab returns to foreground
// Lazy-import getWs to avoid circular dependency (wsConnection imports from ws)
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState !== 'visible') return;

    const { getWs } = await import('./wsConnection');
    const ws = getWs();
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    triggerHealthCheck(ws);
  });
}

// =============================================================================
// COMMAND DISPATCH HELPERS
// =============================================================================

/**
 * Send a command via the dispatcher.
 * This is the preferred way to send commands.
 */
export function send<K extends keyof CommandDefinitions>(
  command: K,
  payload: CommandDefinitions[K],
  corr?: string
): void {
  dispatch(command, payload, corr);
}


